import type {
  AdminMe,
  AdminMeta,
  AuditListResponse,
  Entitlement,
  EntitlementRequest,
  Episode,
  LoginResult,
  ShowAdminDetail,
  ShowSummary,
  SubtitleInput,
  SubtitleUploadResult,
  Taxonomy,
  TaxonomyKind,
  UserDetail,
  UserListResponse,
} from './types'

// 空基址：开发走 Vite 代理，生产与 API 同域。
const BASE = import.meta.env.VITE_API_BASE ?? ''

/** 后端信封 {error:{code,message,request_id}} */
export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly requestId: string

  constructor(status: number, code: string, message: string, requestId = '') {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.requestId = requestId
  }

  /** 401 / invalid_token。登录 403 不是会话失效。 */
  get isSessionInvalid(): boolean {
    return this.status === 401 || this.code === 'invalid_token'
  }
}

type TokenReader = () => string | null
type SessionExpiredHandler = () => void

let readToken: TokenReader = () => null
let onSessionExpired: SessionExpiredHandler = () => {}

/** 由 session 注入，避免循环依赖。 */
export function configureClient(opts: { getToken: TokenReader; onExpired: SessionExpiredHandler }) {
  readToken = opts.getToken
  onSessionExpired = opts.onExpired
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  opts?: { skipAuth?: boolean },
): Promise<T> {
  const headers: Record<string, string> = {}
  const token = opts?.skipAuth ? null : readToken()
  if (token) headers.Authorization = `Bearer ${token}`
  if (body !== undefined) headers['Content-Type'] = 'application/json'

  let res: Response
  try {
    res = await fetch(`${BASE}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    })
  } catch {
    throw new ApiError(0, 'network_error', '无法连接到服务，请检查网络或后端是否在运行')
  }

  if (res.status === 204) return undefined as T

  const text = await res.text()
  let payload: unknown = null
  if (text) {
    try {
      payload = JSON.parse(text)
    } catch {
      payload = null
    }
  }

  if (!res.ok) {
    const envelope = (payload as { error?: { code?: string; message?: string; request_id?: string } })?.error
    const err = new ApiError(
      res.status,
      envelope?.code ?? 'unknown_error',
      envelope?.message ?? `请求失败（HTTP ${res.status}）`,
      envelope?.request_id ?? '',
    )
    // 带票的 forbidden = 后台会话被降权
    if (err.isSessionInvalid || (token != null && err.code === 'forbidden')) onSessionExpired()
    throw err
  }

  return payload as T
}

async function requestText(path: string): Promise<string> {
  const token = readToken()
  const res = await fetch(`${BASE}${path}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  })
  if (!res.ok) {
    if (res.status === 401) onSessionExpired()
    throw new ApiError(res.status, 'subtitle_fetch_failed', '字幕不存在或无权访问')
  }
  return res.text()
}

const qs = (params: Record<string, string | number | undefined>) => {
  const sp = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '') sp.set(k, String(v))
  }
  const s = sp.toString()
  return s ? `?${s}` : ''
}

export const api = {
  login: (email: string, password: string) =>
    request<LoginResult>('POST', '/api/v1/admin/auth/login', { email, password }, { skipAuth: true }),
  logout: () => request<{ message: string }>('POST', '/api/v1/admin/auth/logout'),
  me: () => request<AdminMe>('GET', '/api/v1/admin/me'),
  meta: () => request<AdminMeta>('GET', '/api/v1/admin/meta'),

  listUsers: (query: string, limit = 50, offset = 0) =>
    request<UserListResponse>('GET', `/api/v1/admin/users${qs({ query, limit, offset })}`),
  userDetail: (id: string) => request<UserDetail>('GET', `/api/v1/admin/users/${encodeURIComponent(id)}`),
  grantEntitlement: (id: string, body: EntitlementRequest) =>
    request<Entitlement>('PUT', `/api/v1/admin/users/${encodeURIComponent(id)}/entitlement`, body),
  revokeEntitlement: (id: string) =>
    request<Entitlement>('DELETE', `/api/v1/admin/users/${encodeURIComponent(id)}/entitlement`),

  listShows: () => request<{ shows: ShowSummary[] }>('GET', '/api/v1/admin/shows'),
  listTaxonomies: (kind?: TaxonomyKind) =>
    request<{ taxonomies: Taxonomy[] }>(
      'GET',
      `/api/v1/admin/taxonomies${kind ? qs({ kind }) : ''}`,
    ),
  upsertShow: (
    id: number,
    locales?: { locale: string; title: string; description: string }[],
    taxonomies?: { genres?: string[]; tropes?: string[] },
  ) =>
    request<ShowAdminDetail>('POST', '/api/v1/admin/shows', {
      id,
      ...(locales !== undefined ? { locales } : {}),
      ...(taxonomies?.genres !== undefined ? { genres: taxonomies.genres } : {}),
      ...(taxonomies?.tropes !== undefined ? { tropes: taxonomies.tropes } : {}),
    }),
  showDetail: (id: number) => request<ShowAdminDetail>('GET', `/api/v1/admin/shows/${id}`),
  deleteShow: (id: number) => request<{ message: string }>('DELETE', `/api/v1/admin/shows/${id}`),
  deleteShowLocale: (id: number, locale: string) =>
    request<{ message: string }>('DELETE', `/api/v1/admin/shows/${id}/locales/${encodeURIComponent(locale)}`),

  listSeasonEpisodes: (id: number, season: number) =>
    request<{ show_id: number; season: number; episodes: Episode[] }>(
      'GET',
      `/api/v1/admin/shows/${id}/seasons/${season}/episodes`,
    ),
  deleteEpisode: (id: number, season: number, episode: number) =>
    request<{ message: string }>('DELETE', `/api/v1/admin/shows/${id}/seasons/${season}/episodes/${episode}`),

  uploadSubs: (id: number, season: number, episode: number, subtitles: SubtitleInput[]) =>
    request<SubtitleUploadResult>(
      'PUT',
      `/api/v1/admin/shows/${id}/seasons/${season}/episodes/${episode}/subs`,
      { subtitles },
    ),
  uploadSeasonSubs: (id: number, season: number, subtitles: SubtitleInput[]) =>
    request<SubtitleUploadResult>('POST', `/api/v1/admin/shows/${id}/seasons/${season}/subs`, { subtitles }),
  subtitleContent: (id: number, season: number, episode: number, locale: string) =>
    requestText(`/api/v1/admin/shows/${id}/seasons/${season}/episodes/${episode}/subs/${encodeURIComponent(locale)}`),
  deleteSub: (id: number, season: number, episode: number, locale: string) =>
    request<{ message: string }>(
      'DELETE',
      `/api/v1/admin/shows/${id}/seasons/${season}/episodes/${episode}/subs/${encodeURIComponent(locale)}`,
    ),

  listAudit: (limit = 50, offset = 0) =>
    request<AuditListResponse>('GET', `/api/v1/admin/audit${qs({ limit, offset })}`),
}
